import React, { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAnimationActivity } from 'contexts/AnimationActivityContext';
import { useSeatRotation } from 'contexts/SeatRotationContext';
import { TableIdContext } from 'contexts/TableIdContext';
import gsap from 'gsap';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useTableSnap } from 'hooks/useTableSnap';
import { isTableResyncing } from 'utils/tableResyncState';
import { isRectOnScreen } from 'utils/viewport';

import PokerChip, { getChipColor } from './PokerChip';
import {
  claimWinChipAward,
  discardWinChipContinuation,
  interruptWinChipFlight,
  newWinChipFlightDeadline,
  resumeWinChipFlight
} from './winChipAwards';

type WinChipsPropType = {
  awardId?: string;
  seatId: number;
  originElement: HTMLElement | null;
  seatPosition: SeatPosition;
  potValue?: number;
  allowViewHandoff?: boolean;
  allowFlightContinuation?: boolean;
};

const CHIP_COUNT = 10;
const CHIP_SIZE = 16;

/**
 * Resolve the element that seat positions (% values) are relative to.
 *
 * Priority:
 *   1. Nearest ancestor carrying `[data-table-root]` (set by Table/index.tsx).
 *   2. Outermost ancestor with computed `position: relative` (the *farthest*
 *      one we walk past while traversing up; the first relative ancestor
 *      is usually the small Pot wrapper, which has the wrong dimensions).
 *   3. Final fallback: the start element's immediate parent.
 *
 * Exported for unit testing.
 */
export function findTableContainer(start: HTMLElement | null): HTMLElement | null {
  if (!start) return null;

  const marked = start.closest('[data-table-root]') as HTMLElement | null;
  if (marked) return marked;

  let outermostRelative: HTMLElement | null = null;
  let node: HTMLElement | null = start.parentElement;
  while (node && node !== document.body) {
    const computed = window.getComputedStyle(node);
    if (computed.position === 'relative') outermostRelative = node;
    node = node.parentElement;
  }
  if (outermostRelative) return outermostRelative;

  return start.parentElement;
}

/**
 * Chips fly from pot (this component's origin) to the winning seat.
 * Uses the table container dimensions to calculate the actual pixel distance.
 */
const WinChips = ({
  originElement,
  seatPosition,
  potValue = 100,
  awardId,
  allowViewHandoff = false,
  allowFlightContinuation = false
}: WinChipsPropType) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableId = useContext(TableIdContext);
  const { begin } = useAnimationActivity();
  const shouldSnap = useTableSnap();
  const { shouldHoldForRotation } = useSeatRotation();

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !originElement) return;

    // Snapping (table switch / resync, OR the seat ring just turned / is
    // turning): either the pot was already shipped while the player was
    // elsewhere, or this mount landed in the window around a POV-driven seat
    // rotation, where a stale/duplicate re-delivery of an already-paid award
    // can remount this exact award (EN-10725 — see `shouldHoldForRotation`'s
    // doc comment on SeatRotationContext). Either way: park the chips at the
    // flight's end state (invisible) rather than replaying the payout.
    const snap = shouldSnap() && !(allowViewHandoff && !isTableResyncing(tableId));
    const rotationHold = !snap && shouldHoldForRotation();
    if (snap || rotationHold) {
      if (awardId) {
        discardWinChipContinuation(awardId);
        claimWinChipAward(awardId);
      }
      gsap.set(container.querySelectorAll('.win-chip'), { opacity: 0 });
      return;
    }

    const tableContainer = originElement.closest('[data-game-table-container="true"]');
    const originRect = originElement.getBoundingClientRect();
    const tableRect = tableContainer?.getBoundingClientRect();
    if (!tableRect) return;

    const originX = originRect.left + originRect.width / 2;
    const originY = originRect.top + originRect.height / 2;

    const seatX = tableRect.left + (seatPosition.leftPct / 100) * tableRect.width;
    const seatY = tableRect.top + (seatPosition.topPct / 100) * tableRect.height;
    const dx = seatX - originX;
    const dy = seatY - originY;

    const chips = container.querySelectorAll('.win-chip');
    if (chips.length === 0) return;

    // Claim only after the flight can render. With no authoritative hand ID,
    // omit this cosmetic flight rather than replay an unidentified award.
    if (!awardId) return;
    const claimed = claimWinChipAward(awardId);
    const continuation = !claimed && allowFlightContinuation ? resumeWinChipFlight(awardId) : null;
    if (!claimed && !continuation) return;
    const deadline = continuation?.deadline ?? newWinChipFlightDeadline();

    // Mark the table busy for the flight so the seat rotation holds off.
    const endActivity = begin();
    let completed = false;
    const tl = gsap.timeline({
      onComplete: () => {
        completed = true;
        endActivity();
      }
    });

    chips.forEach((chip, i) => {
      const scatter = 20;
      const scatterX = (Math.random() - 0.5) * scatter;
      const scatterY = (Math.random() - 0.5) * scatter;
      const delay = i * 0.03;
      const arcY = -(30 + Math.random() * 20);

      gsap.set(chip, { x: 0, y: 0, opacity: 0, scale: 0.2, rotation: Math.random() * 40 - 20 });

      tl.to(
        chip,
        {
          x: dx * 0.2 + scatterX,
          y: arcY,
          opacity: 1,
          scale: 1.4,
          rotation: Math.random() * 120 - 60,
          duration: 0.2,
          ease: 'power2.out',
          delay
        },
        0
      )
        .to(
          chip,
          {
            x: dx + scatterX,
            y: dy + scatterY,
            scale: 0.8,
            rotation: 0,
            duration: 0.5,
            ease: 'power2.in'
          },
          0.2 + delay
        )
        .to(
          chip,
          {
            opacity: 0,
            scale: 0.4,
            duration: 0.15,
            ease: 'power1.in'
          },
          0.7 + delay
        );
    });

    if (continuation) tl.progress(continuation.progress);

    return () => {
      if (!completed) interruptWinChipFlight(awardId, deadline);
      endActivity();
      tl.kill();
    };
    // Run exactly once on mount. The parent remounts WinChips when a new
    // flight is needed; including position deps would re-fire the animation
    // on every window resize (positions are recomputed from container size).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chipColors = Array.from({ length: CHIP_COUNT }, (_, i) => {
    const fraction = potValue / Math.max(CHIP_COUNT - i, 1);
    return getChipColor(fraction);
  });

  if (!originElement) return null;

  const originRect = originElement.getBoundingClientRect();

  // Skip the (purely cosmetic) win-chip portal when the originating table is
  // rendered off-screen — e.g. a seated table living in the always-mounted
  // MultiTableGrid panel that's slid out of view while the player is in the
  // lobby. This portal mounts to document.body with position:fixed at the
  // seat's coordinates; for an off-screen table those coords fall outside the
  // viewport, and on iOS WKWebView a fixed element placed outside the viewport
  // grows the scrollable content area, scrolling the page and shoving the
  // (also position:fixed) lobby shell up. The background table keeps
  // processing live game data / sound / timers — we just don't paint chips for
  // a table nobody is looking at. See utils/viewport.ts for the shared rule.
  if (!isRectOnScreen(originRect)) return null;

  // Render inside THIS table's own view container (marked `data-table-view-root`
  // on the single-table page and each grid cell) rather than <body>. The table
  // and grid panels are `transform`ed by SlideContainer, which makes each a
  // stacking context — so a <body>-level layer paints ABOVE the whole panel,
  // and therefore above the in-table Tournament Details panel (z 100) and the
  // grid's inline modals (z 200). That was the reported "chips ship on top of
  // the modal" bug. Living inside the panel lets zIndex 90 sit above the felt
  // but below those in-panel modals; body-portaled overlays (single-view
  // GameModal at 200, lobby Tournament Info at 300) still cover the whole panel,
  // so they stay above the chips too. position:fixed still resolves against the
  // active panel (translated to the identity origin), so the viewport
  // coordinates are unchanged. Falls back to <body> for any surface without the
  // marker (e.g. the /demo-table preview).
  const portalTarget =
    (originElement.closest('[data-table-view-root]') as HTMLElement | null) ?? document.body;

  const originStyle = {
    position: 'fixed' as const,
    top: originRect.top + originRect.height / 2,
    left: originRect.left + originRect.width / 2,
    // Above the felt / seats (low table z-indexes) but below the in-panel
    // modals (Tournament Details 100, grid inline GameModal 200). See the
    // portalTarget note above for why this is evaluated inside the panel.
    zIndex: 90,
    pointerEvents: 'none' as const
  };

  return createPortal(
    <div ref={containerRef} style={originStyle}>
      {chipColors.map((color, i) => (
        <div
          key={i}
          className='win-chip'
          style={{ position: 'absolute', top: -CHIP_SIZE / 2, left: -CHIP_SIZE / 2, opacity: 0 }}>
          <PokerChip size={CHIP_SIZE} color={color} />
        </div>
      ))}
    </div>,
    portalTarget
  );
};

export default WinChips;
